import { apiFetch, apiRoutes } from "./api";

export type PaymentIntentType = "subscription" | "one_time";

export type PaymentStatus = "paid" | "pending" | "failed" | "canceled" | "refunded" | "expired";

export interface BillingEvent {
  id: string;
  externalPaymentId: string;
  reference: string;
  type: PaymentIntentType;
  planName: string;
  amount: number;
  currency: string;
  method: string;
  status: PaymentStatus;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  downloadsRemaining: number | null;
  notes: string | null;
}

export interface PixPaymentResponse {
  paymentId: string;
  qrCode: string | null;
  qrCodeBase64: string | null;
  qrCodeText: string | null;
  ticketUrl: string | null;
  expiresAt: string;
  amount: number;
  currency: string;
  reference: string;
  planName: string;
  status: PaymentStatus;
}

export interface StripeCheckoutResponse {
  sessionId: string;
}

export interface BillingSummary {
  currentPlan: {
    code: string;
    name: string;
    expiresAt: string | null;
    isActive: boolean;
  } | null;
  downloadsRemaining: number;
  downloadsToday: number;
  downloadLimit: number;
  downloadsBalance: number;
  membership: string;
  planExpiresAt: string | null;
}

export interface BillingHistoryResponse {
  summary: BillingSummary;
  events: BillingEvent[];
}

export interface PaymentEventStatusResponse {
  event: BillingEvent;
  downloadsRemaining: number | null;
}

export interface CreatePixPaymentPayload {
  intentType: PaymentIntentType;
  planCode?: string;
  productId?: string;
  productTitle?: string;
  notes?: string;
  artworkId?: string;
  couponCode?: string;
}

export interface CreateStripeCheckoutPayload {
  intentType: PaymentIntentType;
  planCode?: string;
  productId?: string;
  productTitle?: string;
  successUrl?: string;
  cancelUrl?: string;
  couponCode?: string;
}

export interface CreateCardPaymentPayload {
  intentType: PaymentIntentType;
  planCode?: string;
  productId?: string;
  productTitle?: string;
  token: string;
  paymentMethodId: string;
  installments?: number;
  issuerId?: string;
  couponCode?: string;
  payer?: {
    email: string;
    identification?: {
      type: string;
      number: string;
    };
  };
}

export interface CardPaymentResponse {
  paymentId: string;
  status: string;
  statusDetail: string;
  amount: number;
  currency: string;
  reference: string;
  planName: string;
  installments: number;
  lastFourDigits: string | null;
  paymentMethodId: string;
}

export interface MercadoPagoPublicKeyResponse {
  publicKey: string;
}

export const createPixPayment = async (payload: CreatePixPaymentPayload): Promise<PixPaymentResponse> => {
  return apiFetch(apiRoutes.payments.createPix(), {
    method: "POST",
    body: JSON.stringify(payload)
  });
};

export const createCardPayment = async (payload: CreateCardPaymentPayload): Promise<CardPaymentResponse> => {
  return apiFetch("/payments/mercadopago/create-card", {
    method: "POST",
    body: JSON.stringify(payload)
  });
};

export const getMercadoPagoPublicKey = async (): Promise<MercadoPagoPublicKeyResponse> => {
  return apiFetch("/payments/mercadopago/public-key", {
    method: "GET"
  });
};

export const createStripeCheckoutSession = async (
  payload: CreateStripeCheckoutPayload
): Promise<StripeCheckoutResponse> => {
  return apiFetch(apiRoutes.payments.createStripeCheckout(), {
    method: "POST",
    body: JSON.stringify(payload)
  });
};

export const fetchBillingHistory = async (): Promise<BillingHistoryResponse> => {
  return apiFetch(apiRoutes.payments.history(), {
    method: "GET",
    cache: "no-store"
  });
};

export const fetchPaymentEventStatus = async (externalId: string): Promise<PaymentEventStatusResponse> => {
  return apiFetch(apiRoutes.payments.eventStatus(externalId), {
    method: "GET",
    cache: "no-store"
  });
};
